import { useCallback, useMemo } from 'react';
import { useQuery } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import * as TierGroups from '../../constants/groupTags/TierGroups';
import { GET_COMPATIBILITY } from '../queries/compatibilityQuery';
import { getDataFetchingClientRequestState } from '../../dataFetchingClient/utils/getDataFetchingClientRequestState';
import FeatureCompatibilityMatrix from './FeatureCompatibilityMatrix';
import LoadingWrapper from '../../layout/components/LoadingWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function FeatureCompatibilityModal({
  appContext,
  isUngatedForContentHub = false,
  offeringType,
  onClose,
  scopeFeatures,
  tierGroupNames: passedTierGroupNames,
  trackInteraction
}) {
  const isInApp = appContext === 'app';
  const tierGroupNames = useMemo(() => passedTierGroupNames || ['CRM', isUngatedForContentHub ? 'CONTENT' : 'CMS', 'MARKETING', 'OPERATIONS', 'SALES', 'SERVICE'], [isUngatedForContentHub, passedTierGroupNames]);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(GET_COMPATIBILITY, {
    variables: {
      offeringType
    }
  });
  const compatibilityPortalData = useMemo(() => {
    const compatibilityData = data || previousData;
    if (!compatibilityData) {
      return {
        compatibility: {},
        portalTiers: []
      };
    } else {
      return compatibilityData.compatibility;
    }
  }, [data, previousData]);
  const compatibilityRequestState = getDataFetchingClientRequestState(error, loading, !!compatibilityPortalData);
  const getIsFeatureTierCompatible = useCallback((feature, tier) => feature.scopes.every(feature1 => {
    const requiredTiers = compatibilityPortalData.compatibility[feature1];
    return !requiredTiers || !requiredTiers.length || requiredTiers.includes(tier);
  }), [compatibilityPortalData.compatibility]);
  const featuresBasedOnTiers = useMemo(() => {
    return scopeFeatures.map(feature => {
      return {
        text: feature.text,
        tiers: tierGroupNames.reduce((arr, tierGroup) => {
          TierGroups[tierGroup].forEach(tier => {
            if (getIsFeatureTierCompatible(feature, tier)) {
              arr.push(tier);
            }
          });
          return arr;
        }, [])
      };
    });
  }, [getIsFeatureTierCompatible, scopeFeatures, tierGroupNames]);
  return /*#__PURE__*/_jsxs(UIModal, {
    width: "90%",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-common.compatibility.featureCompatibilityModal.header"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      className: "p-all-0",
      children: /*#__PURE__*/_jsx(LoadingWrapper, {
        requestStatus: compatibilityRequestState,
        children: /*#__PURE__*/_jsx(FeatureCompatibilityMatrix, {
          features: featuresBasedOnTiers,
          featureType: unescapedText(`marketplace-ui-common.compatibility.offeringType.${offeringType}`),
          isCustomer: isInApp,
          portalTiers: compatibilityPortalData.portalTiers,
          tierGroupNames: tierGroupNames,
          trackPricingLinkClick: tierGroup => trackInteraction('click compatibility modal pricing link', {
            section: 'overview',
            tierGroup
          })
        })
      })
    })]
  });
}